import { useMemo, useState } from 'react';
import { useAppState } from '../../context/LoadTestContext';
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { fitAxis, zeroAnchoredAxis } from './axisScale';
import { useUnits } from '../units';
import { smoothRows } from '../smoothing';
import { stepLabel } from '../steps';
import { PLATE_ANGLE_COLORS, plateAngleLevel } from '../plateAngle';

// One colour per LVDT, checked for colour-blind separation against each theme's
// background; the average is the theme's text colour, dashed.
export const LVDT_COLORS = {
  light: ['#534AB7', '#D85A30', '#1D9E75', '#185FA5'],
  dark: ['#7F77DD', '#E0703F', '#1D9E75', '#378ADD'],
};
const LVDT_KEYS = ['LVDT1', 'LVDT2', 'LVDT3', 'LVDT4'];
// Past this many steps the band labels would run into each other.
const MAX_LABELLED_STEPS = 15;


function LvdtGraph() {
  const { loading, lvdtGraphData, stepSummaryData, allStepSummaryData, measurementUnits, showUnloadSteps, smoothing, selectedStep } = useAppState();
  const theme = useTheme();
  const units = useUnits();
  const rawMV = measurementUnits?.lvdt_graph === 'mV';
  const valueUnit = rawMV ? 'mV' : units.lengthUnit;
  const valueDecimals = rawMV ? 3 : units.lengthDecimals;
  const minValueSpan = rawMV ? 1 : units.minDeflectionSpan;
  const [hidden, setHidden] = useState(() => new Set());

  // Unload steps (by their number in step_summary), cut from the graph while
  // they are hidden: the time axis then ends with the last load step, and an
  // unload step mid-test leaves a gap rather than a line drawn across it.
  const hiddenSteps = useMemo(() => new Set(showUnloadSteps ? [] : (allStepSummaryData || [])
    .filter((s) => s.kind === 'U').map((s) => Number(s.step))), [allStepSummaryData, showUnloadSteps]);

  // Backend sends each LVDT zeroed to its first reading, in inches, against
  // elapsed minutes; convert into the display unit once.
  const points = useMemo(() => {
    if (rawMV) return [];
    const len = (v) => {
      if (v == null) return null;
      const value = Number(v);
      if (!Number.isFinite(value)) return null;

      // return rawMV ? value : units.toLength(value);
      return units.toLength(value);
    };

    const out = [];
    let gap = false;

    (Array.isArray(lvdtGraphData) ? lvdtGraphData : []).forEach((d) => {
      if (hiddenSteps.has(Number(d?.step))) {
        gap = true;
        return;
      }

      const t = Number(d?.elapsed_min);

      // Break every line across hidden steps.
      if (gap && out.length) {
        out.push({
          t,
          LVDT1: null,
          LVDT2: null,
          LVDT3: null,
          LVDT4: null,
          deflection: null,
        });
      }

      gap = false;

      out.push({
        t,
        step: d?.step,
        load: d?.load,
        LVDT1: len(d?.LVDT1),
        LVDT2: len(d?.LVDT2),
        LVDT3: len(d?.LVDT3),
        LVDT4: len(d?.LVDT4),
        deflection: len(d?.deflection),
        plate_angle_deg: d?.plate_angle_deg,
      });
    });

    return smoothRows(
      out,
      [...LVDT_KEYS, 'deflection'],
      smoothing,
    );
  }, [lvdtGraphData, hiddenSteps, smoothing, units.system, rawMV]);

  // Stretches of readings where the plate angle is at a warning or critical
  // level (see plateAngle.js), shaded in that level's colour. A stretch that
  // goes from orange to red starts its red part where the orange one ended.
  const warningRanges = useMemo(() => {
    const ranges = [];
    let current = null;

    points.forEach((point) => {
      const level = Number.isFinite(point.t) ? plateAngleLevel(point.plate_angle_deg) : null;

      if (!level) {
        current = null;
        return;
      }

      if (current && current.level === level) {
        current.x2 = point.t;
      } else {
        current = { level, x1: current ? current.x2 : point.t, x2: point.t };
        ranges.push(current);
      }
    });

    return ranges;
  }, [points]);

  const hasPlotData = points.some((point) => LVDT_KEYS.some((key) => Number.isFinite(point[key])));

  const emptyMessage = rawMV ? 'Calibrate at least one LVDT to plot deflection' : !hasPlotData ? 'No valid LVDT displacement readings to plot.' : null;

  // Every step's name (S1, U1, ...), and which steps are shown (unload steps
  // only when the switch is on).
  const labels = useMemo(() => new Map(allStepSummaryData.map((s) => [s.step, stepLabel(s)])), [allStepSummaryData]);
  const shown = useMemo(() => new Set(stepSummaryData.map((s) => s.step)), [stepSummaryData]);

  // The shown steps as contiguous time ranges, for shading alternate ones.
  const steps = useMemo(() => {
    const out = [];
    points.forEach((p) => {
      const last = out[out.length - 1];
      if (last && last.step === p.step) last.x2 = p.t;
      else out.push({ step: p.step, x1: p.t, x2: p.t });
    });
    return out.filter((s) => s.step > 0 && shown.has(s.step));
  }, [points, shown]);

  // The picked step's time range, highlighted in the selected card's colour.
  const picked = useMemo(() => {
    if (selectedStep === null || selectedStep === undefined) return null;
    const ts = points.filter((p) => Number(p.step) === Number(selectedStep)).map((p) => p.t);
    return ts.length ? { x1: Math.min(...ts), x2: Math.max(...ts) } : null;
  }, [points, selectedStep]);
  const pickedColor = theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.secondary.main;

  const series = [
    ...LVDT_KEYS.map((key, i) => ({
      key,
      name: `LVDT ${i + 1}`,
      color: LVDT_COLORS[theme.palette.mode === 'dark' ? 'dark' : 'light'][i],
    })),
    { key: 'deflection', name: 'Average', color: theme.palette.text.primary, dash: '6 3', width: 2.5 },
  ];

  // Same scaling as the deflection graph: zero at the top, round steps, room for
  // however far the visible lines have settled.
  const valueAxis = useMemo(() => zeroAnchoredAxis(
    points.flatMap((p) => series.filter((s) => !hidden.has(s.key)).map((s) => p[s.key])),
    { minSpan: minValueSpan },
  ), [points, hidden, units.system, theme.palette.mode, minValueSpan]);

  // Round ticks, but the axis ends at the last reading rather than the next round
  // number, so a live test doesn't leave an empty stretch on the right.
  const timeAxis = useMemo(() => {
    const end = Math.max(0, ...points.map((p) => p.t).filter(Number.isFinite));
    const fitted = fitAxis([0, end]);
    if (!fitted || end <= 0) return null;
    return { ...fitted, domain: [0, end], ticks: fitted.ticks.filter((t) => t <= end) };
  }, [points]);

  const toggle = (key) => setHidden((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else if (next.size < series.length - 1) next.add(key); // keep at least one line
    return next;
  });

  if (loading) {
    return <Skeleton variant='rectangle' animation='wave' fullWidth sx={{ borderRadius: 1, minHeight: 180 }} />;
  }

  // if (points.length === 0) {
  //   return (
  //     <Box sx={{ height: '99%', minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
  //       <Typography variant='body2' color='text.secondary'>No LVDT readings for this test yet.</Typography>
  //     </Box>
  //   );
  // }

  return (
    <Box sx={{ height: '99%', minHeight: 180, userSelect: 'none', position: 'relative' }}>
      {/* In the plot's bottom-left corner: early in the test with a large
          displacement, which the settling lines don't reach. */}
      {!loading && !emptyMessage && (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ position: 'absolute', left: 74, bottom: 48, zIndex: 1, fontSize: 11, pointerEvents: 'none', opacity: 0.8 }}
        >
          Each LVDT zeroed to its first reading
        </Typography>
      )}
      {!loading && emptyMessage && (
        <Box
          role='status'
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 2,
            p: 3,
          }}
        >
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              px: 2,
              py: 1,
              textAlign: 'center'
            }}
          >
            {emptyMessage}
          </Typography>
        </Box>
      )}
      <ResponsiveContainer width='100%' height='99%' minHeight={180}>
        <LineChart data={points} margin={{ top: 5, right: 10, left: 5, bottom: 10 }}>
          <CartesianGrid strokeDasharray='3 3' />

          {steps.map((s, i) => (i % 2 === 0 || steps.length <= MAX_LABELLED_STEPS) && (
            <ReferenceArea
              key={s.step}
              x1={s.x1}
              x2={s.x2}
              // fill={i % 2 === 0 ? theme.palette.action.hover : 'transparent'}
              fill={theme.palette.action.hover}
              fillOpacity={1}
              strokeOpacity={0}
              ifOverflow='hidden'
              label={steps.length <= MAX_LABELLED_STEPS
                ? { value: labels.get(s.step) || `S${s.step}`, position: 'insideTop', fontSize: 10, fill: theme.palette.text.secondary }
                : undefined}
            />
          ))}

          {picked && (
            <ReferenceArea
              x1={picked.x1}
              x2={picked.x2}
              fill={pickedColor}
              fillOpacity={0.18}
              stroke={pickedColor}
              strokeOpacity={0.6}
              ifOverflow='hidden'
            />
          )}

          {warningRanges.map(({ level, x1, x2 }, i) => {
            const color = theme.palette[PLATE_ANGLE_COLORS[level]].main;
            return x1 === x2 ? (
              <ReferenceLine
                key={`plate-warning-${i}`}
                x={x1}
                stroke={color}
                strokeWidth={1}
                strokeOpacity={0.6}
                strokeDasharray='3 3'
                ifOverflow='hidden'
              />
            ) : (
              <ReferenceArea
                key={`plate-warning-${i}`}
                x1={x1}
                x2={x2}
                fill={color}
                fillOpacity={0.14}
                strokeOpacity={0}
                ifOverflow='hidden'
              />
            );
          })}

          <XAxis
            dataKey='t'
            type='number'
            fontSize={10}
            domain={timeAxis?.domain || ['dataMin', 'dataMax']}
            ticks={timeAxis?.ticks}
            tickFormatter={timeAxis?.format}
            interval={0}
          >
            <Label value='Test time (min)' fontSize={12} dy={12} />
          </XAxis>
          <YAxis
            type='number'
            domain={valueAxis.domain}
            ticks={valueAxis.ticks}
            tickFormatter={valueAxis.format}
            interval={0}
            fontSize={10}
          >
            <Label
              value={rawMV ? 'Reading (mV)' : `Displacement (${valueUnit})`}
              fontSize={12}
              verticalAnchor='middle'
              angle={-90}
              dx={-10}
            />
          </YAxis>

          <Tooltip
            contentStyle={{ backgroundColor: theme.chart.tooltipBg, color: theme.palette.text.secondary }}
            formatter={(value, name) => [`${Number(value).toFixed(valueDecimals)} ${valueUnit}`, name]}
            labelFormatter={(t, payload) => {
              const p = payload?.[0]?.payload;
              const step = p?.step > 0 ? ` · Step ${labels.get(p.step) || p.step} · ${units.fmtForce(p.load)} ${units.forceUnit}` : '';
              const level = plateAngleLevel(p?.plate_angle_deg);
              const angle = Number.isFinite(p?.plate_angle_deg) ? ` · Plate angle ${p.plate_angle_deg.toFixed(2)}°${level ? ` - ${level.toUpperCase()}` : ''}` : '';
              return `${Number(t).toFixed(1)} min${step}${angle}`;
            }}
          />

          <Legend
            verticalAlign='top'
            align='right'
            iconType='plainline'
            iconSize={14}
            wrapperStyle={{ fontSize: 12, cursor: 'pointer', paddingBottom: 4 }}
            onClick={(e) => toggle(e.dataKey)}
          />

          {series.map((s) => (
            <Line
              key={s.key}
              dataKey={s.key}
              name={s.name}
              stroke={s.color}
              strokeWidth={s.width || 1.75}
              strokeDasharray={s.dash}
              dot={false}
              hide={hidden.has(s.key)}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default LvdtGraph;
